import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { config, local } from '@accounts/config';
import { Confirmation, Destination, Resume, Second, type Entered } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Provider } from '@accounts/transport';
import { Button, Callout, Loading, Waiting } from '@beyond-js/ui/react';
import { Outage, Problem, Retry, Wordmark } from '@accounts/ui';
import { EmailForm } from './signin/email';
import { explain } from './signin/explain';
import { Footer } from './signin/footer';
import { useMethods } from './signin/methods';

/**
 * The screen someone who is not signed in reaches first.
 *
 * It says as little as it can. It never reveals whether an address is registered — signing in with an
 * unknown address and signing in with a wrong password give the same answer — and when no provider is
 * configured it says signing in is not available instead of offering a button that cannot work. When a
 * product sent the person, it says which, what that product is and what it needs (ARR-25). A sign-in
 * for an account with a second factor continues at `/signin/factor`. The window the session dialog
 * opens arrives with the person's address (`hint`) filled in (ACC-17). Its terms, policy and language
 * selector close it (`Footer`).
 */
export function SignIn() {
	const { text, date, accounts, refresh, language, refusal, ended } = usePresence();
	const [parameters] = useSearchParams();
	const navigate = useNavigate();

	const { providers, reading, unread, load } = useMethods(accounts);
	// An invitation opens sign-in with the address it was sent to, still editable (CNT-02); the session
	// dialog's window with the signed-in person's own (ACC-17)
	const invited = (useLocation().state as { email?: unknown } | null)?.email;
	const hint = parameters.get('hint') ?? undefined;
	const initial = typeof invited === 'string' ? invited : (hint ?? '');
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<{ words: string; error: unknown }>();
	// Back from a provider with something to sign in with: the screen waits under the opening, named as its
	// `signin` step, until the browser leaves or the sign-in fails, so the form is never drawn on the way through
	const [returning, setReturning] = useState(() => Provider.carrying());

	/**
	 * Where a sign-in goes: the second step for an account with a second factor, else on
	 */
	async function entered(answer: Entered) {
		if (answer.factor) {
			Second.keep(answer.factor);
			navigate(`/signin/factor?${parameters}`);
			return;
		}
		const next = onward();
		if (next) {
			// The hand-off resumes where it was interrupted, so the person arrives at the product they
			// were going to rather than at this screen
			globalThis.location.href = next;
			return;
		}
		await refresh();
	}

	// A provider returns either an authorization code, which this tab exchanges with the verifier it
	// kept, or a token in the fragment from a project still configured for the implicit return.
	// Either way it is read once, used once, and removed from the address before anything else runs.
	useEffect(() => {
		const returned = Provider.returned();
		if (returned.error) setFailure({ words: text('signin.refused'), error: undefined });
		if (!returned.token && !returned.code) return;

		setBusy(true);
		const proved = returned.token ? Promise.resolve(returned.token) : accounts.exchange(returned.code as string);
		proved
			.then(token => accounts.enter(token))
			.then(answer => entered(answer as Entered))
			.catch(error => {
				setFailure({ words: explain(error, text, date), error });
				setReturning(false);
			})
			.finally(() => setBusy(false));
		// Once per page load: the provider's return is read once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accounts]);

	/**
	 * Where to go once signed in.
	 *
	 * In the window a page's session opened (`?mode=window`), the landing, which tells that page and
	 * closes the window: the address carries the mode, so a provider's round trip comes back to it.
	 * Otherwise only the hand-off endpoint of this installation's own service is accepted, and only as a path.
	 * Anything else is ignored rather than followed: a return destination is chosen by whoever sent
	 * the person here, and an identity screen that follows one unchecked is an open redirect with a
	 * freshly authenticated victim attached.
	 */
	function onward(): string | undefined {
		return Resume.landed(parameters) ?? Resume.handoff(parameters.get('to'), config.authority);
	}

	// Where signing in leads, said before anyone signs in, and only what this installation can stand behind
	const course = new Destination(parameters, config.authority).course(providers?.products);
	/** What signing in leads to, in words; nothing when there is nothing to say or it is ignored */
	function said(): string | undefined {
		if (!course || course.kind === 'ignored') return undefined;
		if (course.kind === 'product') return text('signin.returning', { product: course.name });
		// An application connecting is never named here: its name is its own claim, said on the consent
		if (course.kind === 'connection') return course.name ? text('signin.connection', { product: course.name }) : text('signin.connection.unnamed');
		return text(course.kind === 'invitation' ? 'signin.invitation' : 'signin.continue');
	}
	const bound = said();
	const about = course?.kind === 'product' ? [course.product.description, course.product.requirements].map(words => words?.[language] ?? words?.en).filter(Boolean) : [];
	// The service says which versions of the terms and policy it records, and then the notice appears
	const presented = Boolean(providers?.legal?.terms || providers?.legal?.privacy);
	const configured = providers?.configured ?? false;
	// Configured but unreachable is its own state: the methods are unknown, not absent
	const unreachable = Boolean(providers?.configured) && providers?.available === false;
	const external = (providers?.providers ?? []).filter(name => name !== 'email');
	const offersEmail = (providers?.providers ?? []).includes('email');
	// The messages lead back to this interface with where the sign-in was going
	const confirmation = new Confirmation(parameters, config.authority);
	const back = { confirm: confirmation.address(globalThis.location.origin), reset: confirmation.address(globalThis.location.origin).replace(Confirmation.PATH, '/reset') };

	if (returning) {
		return (
			<div data-bui-opening-wait="signin" className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
				<Wordmark label="Accounts" />
				<Loading label={text('signin.working')} />
			</div>
		);
	}

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 tabIndex={-1} className="text-title-1 text-ink">{text('signin.title')}</h1>
					<p className="mt-1 text-body text-ink-muted">{text('signin.lead')}</p>
					{bound ? <p className="mt-1 text-body text-ink-muted">{bound}</p> : null}
					{about.map(words => (
						<p key={words} className="mt-1 max-w-reading text-small text-ink-muted">
							{words}
						</p>
					))}
				</div>
			</header>

			{parameters.get('erased') === '1' ? <Callout tone="success" live title={text('signin.erased')} /> : null}
			{ended && parameters.get('erased') !== '1' ? <Callout tone="warning" live title={text(ended)} /> : null}
			{course?.kind === 'ignored' ? <Callout tone="warning" title={text('signin.ignored')} /> : null}
			{refusal?.unreachable ? <Problem error={refusal} title={text('state.unavailable')} /> : null}
			{failure ? <Problem error={failure.error} title={failure.words} /> : null}

			{/* The methods are the screen's one read. It waits with the shared wait, which the opening covers and a quick
			    answer never shows (D185); Accounts not answering it is the screen's body (D163), a refusal its own answer */}
			{reading && !unread ? <Waiting language={language} kind="lines" rows={3} /> : null}
			<Outage whole reads={[{ key: 'methods', title: text('outage.methods'), unread, run: load }]} />
			{unread && !unread.unreachable ? <Retry title={text(unread.key)} label={text('action.retry')} busy={text('state.retrying')} onRetry={load} error={unread} /> : null}

			{!reading && !unread && !configured ? <Callout tone="warning" live title={text('signin.unavailable')} /> : null}

			{!reading && unreachable ? <Callout tone="warning" live title={text('signin.provider.unreachable')} /> : null}

			{busy && !reading ? <Loading label={text('signin.working')} /> : null}

			{configured && external.length ? (
				<div className="flex flex-col gap-2">
					{external.map(name => (
						<Button
							key={name}
							variant="secondary"
							onClick={() => {
								const provider = new Provider(config.provider, config.key);
								void provider.authorize(name, globalThis.location.href).then(where => {
									globalThis.location.href = where;
								});
							}}
						>
							{text('signin.with', { provider: text(`identities.provider.${name}`) })}
						</Button>
					))}
				</div>
			) : null}

			{configured && external.length && offersEmail ? <p className="text-center text-small text-ink-muted">{text('signin.or.email')}</p> : null}

			{configured && offersEmail ? <EmailForm initial={initial} hinted={Boolean(hint)} back={back} onEntered={entered} /> : null}

			{local ? (
				<Link className="-my-1 self-center py-1 text-body text-ink-muted underline" to={`/dev/quicklogin?${parameters}`}>
					{text('quick.link')}
				</Link>
			) : null}

			<Footer presented={presented} />
		</div>
	);
}
